import React, { createContext, useEffect, useState } from "react";
import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
  HubConnectionState,
} from "@microsoft/signalr";
import { EventModel } from "./EventModel";

interface EventHubContextType {
  connection: HubConnection | undefined;
  subscribe?: (e: EventModel, onEventUpdated: (e: EventModel) => void) => void;
}

export const EventHubContext = createContext<EventHubContextType>({
  connection: undefined,
});

export const EventHubProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [connection, setConnection] = useState<HubConnection | undefined>(
    undefined
  );
  const [eventId, setEventId] = useState<string>();
  const [subscribed, setSubscribed] = useState<boolean>(false);

  const subscribe = async (
    e: EventModel,
    onEventUpdated: (e: EventModel) => void
  ): Promise<void> => {
    if (!connection) {
      // wait and retry once connection is available
      setTimeout(() => subscribe(e, onEventUpdated), 500);
      return;
    }

    if (eventId !== e.id && !subscribed) {
      connection.state === HubConnectionState.Connected
        ? connection.invoke("subscribe", e.id).then(() => {
            setEventId(e.id);
            setSubscribed(true);
            console.log(`EventHub: Subscribed to event: ${e.id}`);
          })
        : setTimeout(() => subscribe(e, onEventUpdated), 1000);
    }

    console.log(`EventHub: Setting onEventUpdated: ${e.id}`);
    connection.off("eventupdated");
    connection.on("eventupdated", (updatedEvent: EventModel): void => {
      if (updatedEvent.id === e.id) {
        console.log(`EventHub: Calling onEventUpdated: ${e.id}`);
        onEventUpdated(updatedEvent);
      }
    });
  };

  useEffect(() => {
    let isMounted = true;

    const startConnection = async () => {
      if (!connection) {
        const conn = new HubConnectionBuilder()
          .withUrl("/hub")
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Information)
          .build();

        if (isMounted) setConnection(conn);

        try {
          await conn.start();
          console.log("Connection successfully started");
          conn.on("receivemessage", (m: string) => {
            console.log(`EventHub: Received Message: ${m}`);
          });
          conn.onreconnected(() => {
            if (subscribed) {
              conn.send("subscribe", eventId);
              console.log(`EventHub: Resubscribed to event: ${eventId}`);
            }
          });
        } catch (err) {
          console.log(`EventHub: Error while establishing connection : ${err}`);
        }
      }
    };

    startConnection();

    return () => {
      isMounted = false;
      connection?.stop();
    };
  }, []);

  return (
    <EventHubContext.Provider value={{ connection, subscribe }}>
      {children}
    </EventHubContext.Provider>
  );
};
